import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

const HideNativeLauncher = createGlobalStyle`
  /* Keep the conversation window; replace only its closed launcher/greeting. */
  html body #LeadboosterContainer:not(.openedChat) {
    display: none !important;
  }
`;

const FloatingLauncher = styled.div`
  position: fixed;
  right: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 2147483646;

  button, a {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #0b6bdd;
    color: #fff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    text-decoration: none;
  }

  button:focus-visible, a:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  svg {
    width: 32px;
    height: 30px;
    flex-shrink: 0;
  }

  .status {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

export default function PipedriveChatLauncher() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [ready, setReady] = useState(window.dynamicoreLeadBoosterReady === true);
  const [isOpen, setIsOpen] = useState(window.dynamicoreLeadBoosterOpen === true);
  const [opening, setOpening] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const openRequested = useRef(false);
  const launcherRef = useRef(null);
  const wasOpen = useRef(isOpen);

  useEffect(() => {
    // Only the provider's initialized event guarantees the chat can open.
    const timeout = window.setTimeout(() => setUnavailable(true), 20000);
    const handleReady = () => {
      window.clearTimeout(timeout);
      if (openRequested.current) {
        openRequested.current = false;
        window.LeadBooster.trigger('open');
      }
      setUnavailable(false);
      setReady(true);
    };
    const handleOpened = () => {
      setOpening(false);
      setIsOpen(true);
    };
    const handleClosed = () => {
      setOpening(false);
      setIsOpen(false);
    };
    window.addEventListener('dynamicore:chat-ready', handleReady);
    window.addEventListener('dynamicore:chat-opened', handleOpened);
    window.addEventListener('dynamicore:chat-closed', handleClosed);
    if (window.dynamicoreLeadBoosterReady === true) {
      handleReady();
    }

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('dynamicore:chat-ready', handleReady);
      window.removeEventListener('dynamicore:chat-opened', handleOpened);
      window.removeEventListener('dynamicore:chat-closed', handleClosed);
    };
  }, []);

  useEffect(() => {
    if (wasOpen.current && !isOpen) launcherRef.current?.focus({ preventScroll: true });
    wasOpen.current = isOpen;
  }, [isOpen]);

  const icon = (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 2H7a2 2 0 0 0-2 2v1h13a3 3 0 0 1 3 3v9l1 1V4a2 2 0 0 0-2-2Z" />
      <path d="M17 7H4a2 2 0 0 0-2 2v13l5-4h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2Zm-2 7H6v-2h9v2Z" />
    </svg>
  );

  return (
    <>
      <HideNativeLauncher />
      {!isOpen && (
        <FloatingLauncher>
          {unavailable && !ready ? (
            <a
              ref={launcherRef}
              href={pathname.startsWith('/en/') ? '/en/contact' : '/contacto'}
              title={t('El chat no está disponible. Escríbenos desde contacto.')}
              aria-label={t('El chat no está disponible. Escríbenos desde contacto.')}
            >
              {icon}
            </a>
          ) : (
            <button
              ref={launcherRef}
              type="button"
              aria-label={t('Abrir chat')}
              aria-haspopup="dialog"
              aria-busy={opening}
              title={opening ? t('Cargando chat...') : t('Abrir chat')}
              onClick={() => {
                setOpening(true);
                if (window.dynamicoreLeadBoosterReady === true) {
                  window.LeadBooster.trigger('open');
                } else {
                  openRequested.current = true;
                }
              }}
            >
              {icon}
              <span className="status" aria-live="polite">{opening ? t('Cargando chat...') : ''}</span>
            </button>
          )}
        </FloatingLauncher>
      )}
    </>
  );
}
